// Client API per pannello configurazione (Sprint 5).
import { request } from './_request';

export interface SettingsResponse {
  ok: true;
  settings: {
    instance_env: string;
    display_name: string;
    organization: {
      name: string | null;
      legal_name: string | null;
      vat_or_id: string | null;
      contact_email: string | null;
      contact_phone: string | null;
      address: string | null;
    };
    branding: {
      logo_url: string | null;
      primary_color: string | null;
      secondary_color: string | null;
      font_override: string | null;
    };
    llm: {
      mode: 'managed' | 'client';
      model: string;
      temperature: number;
      max_tokens: number;
      client_api_key_set: boolean;
      client_api_key_preview: string | null;
    };
    limits: {
      monthly_soft: number;
      monthly_hard: number;
      alert_email_recipient: string | null;
    };
    retention_days: number;
    admin_default_language: string;
    privacy: {
      policy_url: string | null;
      dpo_email: string | null;
    };
    smtp: {
      host: string;
      port: number;
      user: string | null;
      from: string;
      secure: boolean;
      password_set: boolean;
      password_preview: string | null;
    } | null;
  };
}

export async function getSettings(): Promise<SettingsResponse> {
  return request<SettingsResponse>('/api/admin/settings', { method: 'GET' });
}

export async function updateIdentity(data: {
  organization_name?: string | null;
  organization_legal_name?: string | null;
  organization_vat_or_id?: string | null;
  organization_contact_email?: string | null;
  organization_contact_phone?: string | null;
  organization_address?: string | null;
  display_name?: string;
}): Promise<{ ok: true }> {
  return request('/api/admin/settings/identity', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function updateBranding(data: {
  brand_logo_url?: string | null;
  brand_primary_color?: string | null;
  brand_secondary_color?: string | null;
  brand_font_override?: string | null;
}): Promise<{ ok: true }> {
  return request('/api/admin/settings/branding', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function uploadBrandingLogo(
  file: File,
): Promise<{ ok: true; url: string; size_bytes: number }> {
  const formData = new FormData();
  formData.append('file', file);
  // NB: non usiamo `request` perché serve multipart, no JSON Content-Type
  const res = await fetch('/api/admin/settings/branding/logo', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const json = await res.json();
  if (!res.ok || !json.ok) {
    throw new Error(json.error ?? 'Upload fallito');
  }
  return json;
}

export async function updateLlm(data: {
  mode?: 'managed' | 'client';
  model?: string;
  temperature?: number;
  max_tokens?: number;
}): Promise<{ ok: true }> {
  return request('/api/admin/settings/llm', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function setLlmClientKey(
  apiKey: string,
): Promise<{ ok: true; preview: string; latency_ms: number }> {
  return request('/api/admin/settings/llm-client-key', {
    method: 'PUT',
    body: JSON.stringify({ api_key: apiKey }),
  });
}

export async function removeLlmClientKey(): Promise<{ ok: true }> {
  return request('/api/admin/settings/llm-client-key', { method: 'DELETE' });
}

export async function updateLimits(data: {
  monthly_soft: number;
  monthly_hard: number;
  alert_email_recipient?: string | null;
}): Promise<{ ok: true }> {
  return request('/api/admin/settings/limits', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function updateRetention(retentionDays: number): Promise<{ ok: true }> {
  return request('/api/admin/settings/retention', {
    method: 'PATCH',
    body: JSON.stringify({ retention_days: retentionDays }),
  });
}

export async function updateSmtp(data: {
  host: string;
  port: number;
  user: string;
  password?: string;
  from: string;
  secure: boolean;
}): Promise<{ ok: true }> {
  return request('/api/admin/settings/smtp', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function testSmtp(): Promise<{ ok: boolean; message: string }> {
  return request('/api/admin/settings/smtp/test', { method: 'POST' });
}

export async function updatePrivacy(data: {
  privacy_policy_url?: string | null;
  privacy_dpo_email?: string | null;
}): Promise<{ ok: true }> {
  return request('/api/admin/settings/privacy', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Sprint 8 — voice mode (STT/TTS)
// ============================================================================

export interface VoiceSettingsResponse {
  ok: true;
  voice: {
    enabled: boolean;
    stt: { provider: 'deepgram'; mode: 'managed' | 'client'; model: string };
    tts: {
      provider: 'elevenlabs';
      mode: 'managed' | 'client';
      voice_id: string | null;
      model: string;
    };
    limits: {
      monthly_audio_minutes_soft?: number;
      monthly_audio_minutes_hard?: number;
    };
  };
  stt_client_key_set: boolean;
  tts_client_key_set: boolean;
}

export async function getVoiceSettings(): Promise<VoiceSettingsResponse> {
  return request('/api/admin/settings/voice', { method: 'GET' });
}

export async function updateVoiceSettings(data: {
  enabled?: boolean;
  stt?: { provider?: 'deepgram'; mode?: 'managed' | 'client'; model?: string };
  tts?: {
    provider?: 'elevenlabs';
    mode?: 'managed' | 'client';
    voice_id?: string | null;
    model?: string;
  };
  limits?: {
    monthly_audio_minutes_soft?: number | null;
    monthly_audio_minutes_hard?: number | null;
  };
}): Promise<{ ok: true; voice: VoiceSettingsResponse['voice'] }> {
  return request('/api/admin/settings/voice', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function setVoiceSttKey(apiKey: string): Promise<{ ok: true; latency_ms: number }> {
  return request('/api/admin/settings/voice/stt-client-key', {
    method: 'PUT',
    body: JSON.stringify({ api_key: apiKey }),
  });
}

export async function removeVoiceSttKey(): Promise<{ ok: true }> {
  return request('/api/admin/settings/voice/stt-client-key', { method: 'DELETE' });
}

export async function setVoiceTtsKey(
  apiKey: string,
): Promise<{ ok: true; latency_ms: number; voices?: number }> {
  return request('/api/admin/settings/voice/tts-client-key', {
    method: 'PUT',
    body: JSON.stringify({ api_key: apiKey }),
  });
}

export async function removeVoiceTtsKey(): Promise<{ ok: true }> {
  return request('/api/admin/settings/voice/tts-client-key', { method: 'DELETE' });
}

export interface VoiceListItem {
  voice_id: string;
  name: string;
  preview_url?: string | null;
  labels?: Record<string, string> | null;
  language?: string | null;
}

export async function listVoiceVoices(): Promise<{ ok: true; voices: VoiceListItem[] }> {
  return request('/api/admin/settings/voice/voices', { method: 'GET' });
}

// ============================================================================
// Admin users management
// ============================================================================

export interface AdminUserRow {
  id: string;
  email: string;
  role: 'master' | 'operator' | 'viewer';
  last_login_at: string | null;
  locked_until: string | null;
  totp_enabled: boolean;
  totp_setup_at: string | null;
  must_setup_totp: boolean;
  must_change_password: boolean;
  grace_until: string | null;
  failed_login_count: number;
  created_at: string;
}

export async function listAdminUsers(): Promise<{ ok: true; admins: AdminUserRow[] }> {
  return request('/api/admin/admin-users', { method: 'GET' });
}

export async function createAdminUser(data: {
  email: string;
  role: 'master' | 'operator' | 'viewer';
  send_invite_email: boolean;
}): Promise<{
  ok: true;
  admin: { id: string; email: string; role: string };
  temporary_password: string | null;
  invite_email_sent: boolean;
}> {
  return request('/api/admin/admin-users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function resetAdminPassword(
  id: string,
  sendEmail: boolean,
): Promise<{ ok: true; temporary_password: string | null; email_sent: boolean }> {
  return request(`/api/admin/admin-users/${id}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ send_email: sendEmail }),
  });
}

export async function reset2faAdmin(id: string): Promise<{ ok: true }> {
  return request(`/api/admin/admin-users/${id}/reset-2fa`, { method: 'POST' });
}

export async function lockAdmin(id: string): Promise<{ ok: true }> {
  return request(`/api/admin/admin-users/${id}/lock`, { method: 'POST' });
}

export async function unlockAdmin(id: string): Promise<{ ok: true }> {
  return request(`/api/admin/admin-users/${id}/unlock`, { method: 'POST' });
}

export async function deleteAdmin(id: string): Promise<{ ok: true }> {
  return request(`/api/admin/admin-users/${id}`, { method: 'DELETE' });
}

// ============================================================================
// 2FA TOTP
// ============================================================================

export interface TotpSetupInitResponse {
  ok: true;
  secret: string;
  otpauth_uri: string;
  recovery_codes: string[];
}

export async function totpSetupInit(): Promise<TotpSetupInitResponse> {
  return request('/api/admin/auth/totp/setup-init', { method: 'POST' });
}

export async function totpSetupVerify(code: string): Promise<{ ok: true }> {
  return request('/api/admin/auth/totp/setup-verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function totpDisable(currentCode: string): Promise<{ ok: true }> {
  return request('/api/admin/auth/totp/disable', {
    method: 'POST',
    body: JSON.stringify({ current_code: currentCode }),
  });
}

export async function totpRegenerateRecovery(
  currentCode: string,
): Promise<{ ok: true; recovery_codes: string[] }> {
  return request('/api/admin/auth/totp/regenerate-recovery', {
    method: 'POST',
    body: JSON.stringify({ current_code: currentCode }),
  });
}

export interface TotpStatus {
  ok: true;
  totp_enabled: boolean;
  totp_setup_at: string | null;
  must_setup_totp: boolean;
  grace_until: string | null;
  recovery_codes_remaining: number;
}

export async function totpStatus(): Promise<TotpStatus> {
  return request('/api/admin/auth/totp/status', { method: 'GET' });
}

// ============================================================================
// Audit log viewer
// ============================================================================

export interface AuditLogRow {
  id: string;
  actor_type: 'admin' | 'system' | 'patient';
  actor_id: string | null;
  actor_email: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

export interface AuditLogListResponse {
  ok: true;
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  rows: AuditLogRow[];
}

export async function listAuditLog(params: {
  page?: number;
  page_size?: number;
  actor_type?: 'admin' | 'system' | 'patient';
  action?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}): Promise<AuditLogListResponse> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  return request(`/api/admin/audit-log?${qs.toString()}`, { method: 'GET' });
}

export async function listAuditActions(): Promise<{
  ok: true;
  actions: Array<{ action: string; count: number }>;
}> {
  return request('/api/admin/audit-log/actions', { method: 'GET' });
}
